<template>
  <div class="card" data-testid="year-totals">
    <div class="card-header bg-transparent border-0 pb-0">
      <h5 class="mb-1">{{ $t("forwards.overview.year_totals.title") }}</h5>
      <p class="text-body-tertiary fs-10 mb-0">
        {{ $t("forwards.overview.year_totals.description") }}
      </p>
    </div>
    <div class="card-body">
      <LoadingIndicatorComponent
        v-if="loading"
        :message="$t('forwards.overview.year_totals.loading')"
      />

      <RetryableErrorNoteComponent
        v-else-if="errorMessage"
        :message="errorMessage"
        @retry="emit('retry')"
      />

      <p v-else-if="totals.length === 0" class="text-body-tertiary mb-0 py-3">
        {{ $t("forwards.overview.year_totals.empty") }}
      </p>

      <ul v-else class="mb-0 ps-3 year-totals">
        <li
          v-for="total in totals"
          :key="total.year"
          :data-testid="`year-total-${total.year}`"
        >
          <strong>{{ total.year }}</strong>
          ·
          {{
            $t("forwards.overview.year_totals.volume", {
              mwh: formatNumberWithSpaces(total.priced_mwh),
            })
          }}
          ·
          {{
            total.avg_price === null
              ? $t("forwards.overview.year_totals.no_average")
              : $t("forwards.overview.year_totals.average", {
                  price: total.avg_price,
                })
          }}
          <span
            v-if="total.unpriced_mwh > 0"
            class="text-warning-emphasis"
            data-testid="year-total-unpriced"
          >
            ·
            {{
              $t("forwards.overview.year_totals.unpriced", {
                mwh: formatNumberWithSpaces(total.unpriced_mwh),
              })
            }}
          </span>
        </li>
      </ul>

      <div
        class="text-body-tertiary fs-10 mt-3"
        data-testid="year-totals-contents"
      >
        <p class="mb-0">
          {{ $t("forwards.overview.year_totals.contents.intro") }}
        </p>
        <ul class="mb-0 ps-3" data-testid="year-totals-deal-types">
          <li>
            {{ $t("forwards.overview.year_totals.contents.fixed_volume") }}
          </li>
          <li>
            {{ $t("forwards.overview.year_totals.contents.pay_as_produced") }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import RetryableErrorNoteComponent from "@/forwards2/components/RetryableErrorNoteComponent.vue";
import { forwardsErrorMessage } from "@/forwards2/forwardsApi";
import type { ForwardYearTotal } from "@/forwards2/forwardsTypes";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Forwards v1's per-year totals, as a card: for each production year, the forward volume sold and
 * its average buyer price, with what that volume contains spelled out beneath. The volume is priced
 * volume only, so the two multiply to the value of what is sold; volume still waiting for a buyer
 * price is appended to its year when there is any, and the Needs attention card lists the
 * deliveries behind it.
 */
const props = withDefaults(
  defineProps<{
    totals: ForwardYearTotal[];
    loading?: boolean;
    error?: unknown;
  }>(),
  { loading: false }
);

const emit = defineEmits<{
  retry: [];
}>();

const { t } = useI18n();

const errorMessage = computed(() => {
  if (props.error === null || props.error === undefined) return null;
  return (
    forwardsErrorMessage(props.error) ??
    t("forwards.overview.year_totals.load_failed")
  );
});
</script>

<style scoped lang="scss">
// Bootstrap has no tabular-figures utility; without it the years' digits do not line up.
.year-totals {
  font-variant-numeric: tabular-nums;
}
</style>
